// ══════════════════════════════════════════════════════════════════════════════
// PAGE REJOINDRE UN GROUPE
// Accessible via lien d'invitation /rejoindre-groupe/:inviteCode
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Loader2, Check, X, LogIn } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JoinGroupPage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    fetchGroupInfo();
  }, [inviteCode]);

  const fetchGroupInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/groups/info/${inviteCode}`);
      
      if (response.ok) {
        const data = await response.json();
        setGroup(data);
      } else {
        setError('Ce lien d\'invitation est invalide ou a expiré.');
      }
    } catch (error) {
      setError('Impossible de charger les informations du groupe.');
    } finally {
      setLoading(false);
    }
  };

  const joinGroup = async () => {
    const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
    
    if (!token) {
      // Rediriger vers connexion avec retour après
      toast.info('Connecte-toi d\'abord pour rejoindre le groupe');
      navigate(`/connexion?redirect=/rejoindre-groupe/${inviteCode}`);
      return;
    }
    
    setJoining(true);
    
    try {
      const response = await fetch(`${API_URL}/api/groups/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invite_code: inviteCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setJoined(true);
        toast.success(data.message);
        
        // Rediriger vers le groupe après 2 secondes
        setTimeout(() => {
          navigate(`/cockpit-particulier/groupes/${data.group_id}`);
        }, 2000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setJoining(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-600">Chargement de l'invitation...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl"
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Lien invalide</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-orange-500 to-amber-500"
          >
            Retour à l'accueil
          </Button>
        </motion.div>
      </div>
    );
  }

  // Joined successfully
  if (joined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center"
          >
            <Check className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Bienvenue !</h1>
          <p className="text-slate-600 mb-2">Tu fais maintenant partie du groupe</p>
          <p className="text-xl font-bold text-green-600">{group?.name}</p>
          <p className="text-slate-400 text-sm mt-4">Redirection en cours...</p>
        </motion.div>
      </div>
    );
  }

  // Group preview
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl overflow-hidden max-w-md w-full shadow-xl"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-8 text-white text-center">
          <span className="text-6xl block mb-4">{group?.emoji || '👥'}</span>
          <h1 className="text-2xl font-bold">{group?.name}</h1>
          {group?.description && (
            <p className="text-white/80 mt-2">{group.description}</p>
          )}
        </div>
        
        {/* Info */}
        <div className="p-6">
          <div className="flex items-center justify-center gap-6 text-slate-600 mb-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-800">{group?.member_count || 0}</p>
              <p className="text-sm">Membres</p>
            </div>
            <div className="w-px h-10 bg-slate-200" />
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-800 capitalize">{group?.type}</p>
              <p className="text-sm">Type</p>
            </div>
          </div>
          
          <p className="text-center text-slate-600 mb-6">
            <strong>{group?.created_by_name}</strong> t'invite à rejoindre ce groupe
          </p>
          
          <Button 
            onClick={joinGroup}
            disabled={joining}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 py-6 text-lg"
            data-testid="join-group-btn"
          >
            {joining ? (
              <>
                <Loader2 size={20} className="mr-2 animate-spin" />
                Inscription...
              </>
            ) : (
              <>
                <Users size={20} className="mr-2" />
                Rejoindre le groupe
              </>
            )}
          </Button>
          
          <p className="text-center text-xs text-slate-400 mt-4">
            En rejoignant, tu acceptes les règles du groupe et la charte Abracadatchat
          </p>
        </div>
      </motion.div>
    </div>
  );
}
